/**
 * batch-62 telefoonabonnementen (FO-463) - pure regels van het telefonie-register.
 *
 * Gedeeld door API (register-queries, contract-cron, providerfactuur-controle,
 * import) en backoffice (weergave). Geen I/O, geen datum-library: datums reizen
 * als `jjjj-mm-dd`-strings (NL-kalenderdagen) en bedragen als Decimal-strings die
 * hier in hele centen (integers) worden opgeteld - nooit float-rekenwerk op geld.
 *
 * Inhoud:
 *  - `telefoonRegisterStatus`   afgeleide status IN_GEBRUIK/IN_VOORRAAD/... (geen kolom)
 *  - `bepaalContractSignalen`   contract loopt af / opzegtermijn nadert / stilzwijgend
 *  - `stilzwijgendeVerlenging`  nieuwe einddatum na een stilzwijgende verlenging
 *  - `maandlastenPerBv`         maandlasten-totaal per BV met "onvolledig"-telling
 *  - `telefoonFactuurSluit`     totaalcontrole providerfactuur (regels + btw = totaal)
 *  - `telefoonMaandlast` / `telefoonAfwijking`  afwijkingscontrole per nummer
 *  - import-parsers (status, opzegtermijn, verlengingsregime, type)
 */

import {
  TELEFOON_MAANDLAST_KOSTENSOORTEN,
  abonnementAfwijkingBovenDrempel,
  type AbonnementStatus,
  type AbonnementVerlengingsregime,
  type TelefonieAbonnementType,
  type TelefonieContractSignaal,
  type TelefoonImportRegisterStatus,
  type TelefoonKostensoort,
  type TelefoonRegisterStatus,
} from '@streep/types'
import { dagenTussenNlDagen } from './certificaat-status'

// ─── Register-status ─────────────────────────────────────────────────────────

/**
 * De register-status is AFGELEID (één waarheid): het contract (AbonnementStatus)
 * plus de huidige gebruiker. ACTIEF met medewerker = IN_GEBRUIK, ACTIEF zonder =
 * IN_VOORRAAD (geparkeerd nummer: facturen blijven op het abonnement boeken).
 */
export function telefoonRegisterStatus(a: {
  status: AbonnementStatus
  employeeId: string | null
}): TelefoonRegisterStatus {
  switch (a.status) {
    case 'ACTIEF':
      return a.employeeId ? 'IN_GEBRUIK' : 'IN_VOORRAAD'
    case 'GEBLOKKEERD':
      return 'GEBLOKKEERD'
    case 'OPGEZEGD':
      return 'OPGEZEGD'
    case 'VERLOPEN':
      return 'VERLOPEN'
    case 'CONCEPT':
      return 'CONCEPT'
  }
}

// ─── Datum-helpers (jjjj-mm-dd, NL-kalenderdagen) ────────────────────────────

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function parseYmd(ymd: string): { y: number; m: number; d: number } {
  const m = YMD_RE.exec(ymd)
  if (!m) throw new Error(`Ongeldige datum (verwacht jjjj-mm-dd): ${ymd}`)
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) }
}

function ymdVan(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function dagenInMaand(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** Tel dagen op bij een jjjj-mm-dd (negatief mag). */
export function plusDagenYmd(ymd: string, dagen: number): string {
  const { y, m, d } = parseYmd(ymd)
  const t = new Date(Date.UTC(y, m - 1, d + dagen))
  return ymdVan(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())
}

/**
 * Tel maanden op bij een jjjj-mm-dd, geklemd op het einde van de maand
 * (31-01 + 1 maand = 28-02/29-02, niet 03-03 zoals een kale Date.UTC-overloop).
 */
export function plusMaandenYmd(ymd: string, maanden: number): string {
  const { y, m, d } = parseYmd(ymd)
  const totaal = y * 12 + (m - 1) + maanden
  const ny = Math.floor(totaal / 12)
  const nm = (totaal % 12) + 1
  return ymdVan(ny, nm, Math.min(d, dagenInMaand(ny, nm)))
}

/** `jjjj-mm` van een `jjjj-mm-dd`. */
export function maandVanYmd(ymd: string): string {
  return ymd.slice(0, 7)
}

/** Eerste en laatste dag (jjjj-mm-dd) van een `jjjj-mm`. */
export function maandGrenzen(maand: string): { van: string; tot: string } {
  const [y, m] = maand.split('-').map(Number)
  return { van: ymdVan(y, m, 1), tot: ymdVan(y, m, dagenInMaand(y, m)) }
}

/** Opzegdeadline = einddatum - opzegtermijn (zonder opzegtermijn: de einddatum zelf). */
export function opzegDeadlineYmd(einddatum: string, opzegtermijnDagen: number | null): string {
  return opzegtermijnDagen && opzegtermijnDagen > 0 ? plusDagenYmd(einddatum, -opzegtermijnDagen) : einddatum
}

// ─── Contract-/looptijdsignalen ──────────────────────────────────────────────

export interface ContractSignaalInput {
  status: AbonnementStatus
  /** jjjj-mm-dd of `null` (geen einddatum = geen looptijdsignaal). */
  einddatum: string | null
  opzegtermijnDagen: number | null
  verlengingsregime: AbonnementVerlengingsregime | null
}

export interface ContractSignaalInstelling {
  signaalDagenContractEinde: number
  signaalDagenOpzegDeadline: number
}

export interface ContractSignaalUitkomst {
  soort: Exclude<TelefonieContractSignaal, 'STILZWIJGEND_VERLENGD'>
  /** Het moment waar het signaal over gaat (einddatum of opzegdeadline), jjjj-mm-dd. */
  peildatum: string
  /** Dagen van vandaag tot de peildatum (0 = vandaag). */
  dagen: number
}

/**
 * Welke looptijdsignalen gelden VANDAAG voor dit contract (FO-463 §2.4)?
 *
 *  - VAST (of niet vastgelegd): "contract loopt af" binnen X dagen vóór de einddatum.
 *  - STILZWIJGEND: "wordt stilzwijgend verlengd" binnen X dagen vóór de einddatum
 *    (het moment van verlengen zichtbaar, zodat er nog opgezegd kan worden).
 *  - Met opzegtermijn (beide regimes): "opzegtermijn nadert" binnen Y dagen vóór
 *    de opzegdeadline (einddatum - opzegtermijn).
 *
 * Alleen lopende contracten (ACTIEF/GEBLOKKEERD) met een einddatum. Een verstreken
 * moment (dagen < 0) geeft geen signaal meer: dat handelt de verloop-cron af
 * (VERLOPEN of stilzwijgend verlengd).
 */
export function bepaalContractSignalen(
  input: ContractSignaalInput,
  vandaag: string,
  instelling: ContractSignaalInstelling,
): ContractSignaalUitkomst[] {
  if (input.status !== 'ACTIEF' && input.status !== 'GEBLOKKEERD') return []
  if (!input.einddatum) return []
  const uitkomst: ContractSignaalUitkomst[] = []

  const dagenTotEinde = dagenTussenNlDagen(vandaag, input.einddatum)
  if (dagenTotEinde >= 0 && dagenTotEinde <= instelling.signaalDagenContractEinde) {
    uitkomst.push({
      soort: input.verlengingsregime === 'STILZWIJGEND' ? 'STILZWIJGENDE_VERLENGING' : 'CONTRACT_LOOPT_AF',
      peildatum: input.einddatum,
      dagen: dagenTotEinde,
    })
  }

  if (input.opzegtermijnDagen && input.opzegtermijnDagen > 0) {
    const deadline = opzegDeadlineYmd(input.einddatum, input.opzegtermijnDagen)
    const dagenTotDeadline = dagenTussenNlDagen(vandaag, deadline)
    if (dagenTotDeadline >= 0 && dagenTotDeadline <= instelling.signaalDagenOpzegDeadline) {
      uitkomst.push({ soort: 'OPZEGTERMIJN_NADERT', peildatum: deadline, dagen: dagenTotDeadline })
    }
  }
  return uitkomst
}

/**
 * Stilzwijgende verlenging: is de einddatum verstreken (vóór vandaag), dan schuift
 * hij door met de verlengingsperiode (leeg = looptijd, anders 12 maanden) tot hij
 * weer op of na vandaag ligt. Altijd vanaf de oorspronkelijke einddatum gerekend
 * (n x periode), zodat het klemmen op het maandeinde niet opstapelt.
 *
 * @returns `null` als de einddatum nog niet verstreken is.
 */
export function stilzwijgendeVerlenging(
  input: { einddatum: string; verlengingsPeriodeMaanden: number | null; looptijdMaanden: number | null },
  vandaag: string,
): { nieuweEinddatum: string; periodes: number; periodeMaanden: number } | null {
  if (input.einddatum >= vandaag) return null
  const periodeMaanden =
    input.verlengingsPeriodeMaanden && input.verlengingsPeriodeMaanden > 0
      ? input.verlengingsPeriodeMaanden
      : input.looptijdMaanden && input.looptijdMaanden > 0
        ? input.looptijdMaanden
        : 12
  let periodes = 0
  let eind = input.einddatum
  // Noodrem: 100 jaar maandperiodes.
  while (eind < vandaag && periodes < 1200) {
    periodes++
    eind = plusMaandenYmd(input.einddatum, periodeMaanden * periodes)
  }
  return { nieuweEinddatum: eind, periodes, periodeMaanden }
}

// ─── Geld in centen (Decimal-strings, geen float) ────────────────────────────

/**
 * Decimal-string ("35.00", "-4.5", "1234") naar hele centen. Anders dan
 * `bedragNaarCenten` (kosten-verdeling, leeg = 0) maakt deze variant het verschil
 * tussen "0,00" en "nog niet ingevuld" zichtbaar: dat laatste is `null`. Meer dan twee
 * decimalen wordt afgekapt (Decimal(10,2)-kolommen leveren er nooit meer).
 * `null`/leeg/onleesbaar -> `null`.
 */
export function bedragNaarCentenOfNull(bedrag: string | null | undefined): number | null {
  if (bedrag === null || bedrag === undefined) return null
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(bedrag.trim())
  if (!m) return null
  const heel = Number(m[2])
  const frac = (m[3] ?? '').padEnd(2, '0').slice(0, 2)
  const centen = heel * 100 + Number(frac)
  return m[1] ? -centen : centen
}

// Terug naar een Decimal-string: `centenNaarBedrag` uit ./kosten-verdeling (één helper).

// ─── Maandlasten per BV ──────────────────────────────────────────────────────

export interface MaandlastRij {
  entityId: string | null
  /** Decimal-string of `null` (nog niet ingevuld). */
  maandbedragExclBtw: string | null
}

export interface BvMaandlast {
  entityId: string | null
  aantal: number
  maandlastCenten: number
  aantalZonderBedrag: number
}

/**
 * Maandlasten-totaal per BV (FO-463 §2.6 + §5.5). Rijen zonder bedrag tellen mee
 * in `aantal` en in `aantalZonderBedrag`, NIET in het totaal: zolang er één
 * ontbreekt is het totaal van die BV "onvolledig" (de UI toont dat met de telling,
 * nooit een stil te laag bedrag). Volgorde: hoogste maandlast eerst, "zonder BV"
 * altijd achteraan.
 */
export function maandlastenPerBv(rijen: readonly MaandlastRij[]): {
  perBv: BvMaandlast[]
  totaalCenten: number
  aantalZonderBedrag: number
} {
  const perBv = new Map<string, BvMaandlast>()
  let totaalCenten = 0
  let aantalZonderBedrag = 0
  for (const r of rijen) {
    const sleutel = r.entityId ?? ''
    let bv = perBv.get(sleutel)
    if (!bv) {
      bv = { entityId: r.entityId, aantal: 0, maandlastCenten: 0, aantalZonderBedrag: 0 }
      perBv.set(sleutel, bv)
    }
    bv.aantal++
    const centen = bedragNaarCentenOfNull(r.maandbedragExclBtw)
    if (centen === null) {
      bv.aantalZonderBedrag++
      aantalZonderBedrag++
    } else {
      bv.maandlastCenten += centen
      totaalCenten += centen
    }
  }
  const lijst = [...perBv.values()].sort((a, b) => {
    if ((a.entityId === null) !== (b.entityId === null)) return a.entityId === null ? 1 : -1
    return b.maandlastCenten - a.maandlastCenten
  })
  return { perBv: lijst, totaalCenten, aantalZonderBedrag }
}

// ─── Providerfactuur: totaalcontrole + afwijking ─────────────────────────────

export interface TelefoonFactuurSluitInput {
  /** Bedragen excl. btw van alle uitgelezen regels, in centen. */
  regelCenten: readonly number[]
  /** Factuurkop: subtotaal excl. btw, btw en totaal (centen). */
  factuurExclCenten: number
  factuurBtwCenten: number
  factuurTotaalCenten: number
  /** Tolerantie per controle (default 1 cent). */
  tolerantieCenten?: number
}

export interface TelefoonFactuurSluitUitkomst {
  sluit: boolean
  somRegelsCenten: number
  /** (som regels + btw) - factuurtotaal. 0 = sluit. */
  verschilCenten: number
  /** Klopt de factuurkop zelf (excl + btw = totaal)? */
  kopSluit: boolean
}

/**
 * "Totaal moet sluiten" (FO-463 §2.8): som van de regels + btw = factuurtotaal,
 * en de factuurkop zelf moet kloppen. Anders blijft de factuur "te controleren"
 * (nooit half boeken).
 */
export function telefoonFactuurSluit(input: TelefoonFactuurSluitInput): TelefoonFactuurSluitUitkomst {
  const tolerantie = input.tolerantieCenten ?? 1
  const somRegelsCenten = input.regelCenten.reduce((s, c) => s + c, 0)
  const verschilCenten = somRegelsCenten + input.factuurBtwCenten - input.factuurTotaalCenten
  const kopSluit =
    Math.abs(input.factuurExclCenten + input.factuurBtwCenten - input.factuurTotaalCenten) <= tolerantie
  return {
    sluit: kopSluit && Math.abs(verschilCenten) <= tolerantie,
    somRegelsCenten,
    verschilCenten,
    kopSluit,
  }
}

/** Maandlast (excl. toestel), toestelkosten en totaal van de regels van één nummer. */
export function telefoonMaandlast(
  regels: readonly { kostensoort: TelefoonKostensoort; bedragCenten: number }[],
): { maandlastCenten: number; toestelCenten: number; totaalCenten: number } {
  let maandlastCenten = 0
  let toestelCenten = 0
  for (const r of regels) {
    if (TELEFOON_MAANDLAST_KOSTENSOORTEN.includes(r.kostensoort)) maandlastCenten += r.bedragCenten
    else toestelCenten += r.bedragCenten
  }
  return { maandlastCenten, toestelCenten, totaalCenten: maandlastCenten + toestelCenten }
}

/**
 * Afwijking van het gefactureerde maandbedrag t.o.v. het geregistreerde
 * abonnementsbedrag (FO-463 §2.10), met de bestaande FO-314-drempel (EUR of %).
 * Zonder geregistreerd bedrag valt er niets te vergelijken: `verschilCenten` null.
 */
export function telefoonAfwijking(
  maandlastCenten: number,
  maandbedragCenten: number | null,
): { verschilCenten: number | null; bovenDrempel: boolean } {
  if (maandbedragCenten === null) return { verschilCenten: null, bovenDrempel: false }
  const verschilCenten = maandlastCenten - maandbedragCenten
  return {
    verschilCenten,
    bovenDrempel: abonnementAfwijkingBovenDrempel(verschilCenten / 100, maandbedragCenten / 100),
  }
}

// ─── Import-parsers (vrije tekst uit het nummeroverzicht) ────────────────────

// Combinerende diakrieten (U+0300 t/m U+036F) na NFD-normalisatie. Bewust via
// fromCharCode opgebouwd: een escape-reeks in de bron raakte eerder dubbel
// ge-escaped en haalde dan ook cijfers en hoofdletters weg ("1 maand" -> " maand").
const DIAKRIETEN = new RegExp(`[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`, 'g')

function schoon(raw: string | null | undefined): string {
  return (raw ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(DIAKRIETEN, '')
}

/**
 * Status uit de importlijst: "in gebruik"/"uitgegeven"/"actief" -> IN_GEBRUIK;
 * "voorraad"/"reserve"/"vrij"/"geparkeerd" -> IN_VOORRAAD; "opgezegd"/"beeindigd"
 * -> OPGEZEGD; "geblokkeerd"/"blokkade" -> GEBLOKKEERD. Onbekend/leeg -> `null`
 * (de import leidt het dan af uit de medewerker-kolom).
 */
export function parseTelefoonImportStatus(raw: string | null | undefined): TelefoonImportRegisterStatus | null {
  const s = schoon(raw).replace(/[_-]/g, ' ')
  if (!s) return null
  if (/geblokkeerd|blokkade|blocked/.test(s)) return 'GEBLOKKEERD'
  if (/opgezegd|beeindigd|stopgezet|afgesloten|cancelled/.test(s)) return 'OPGEZEGD'
  // Ontkenningen eerst (review batch-62): "niet in gebruik", "geen gebruiker",
  // "inactief", "niet actief", "uit gebruik" zijn voorraad, geen gebruik.
  if (/(^| )(niet|geen)( |$)|inactief|inactive|uit gebruik|ongebruikt|unused|not in use/.test(s)) return 'IN_VOORRAAD'
  if (/voorraad|reserve|vrij|geparkeerd|beschikbaar|stock/.test(s)) return 'IN_VOORRAAD'
  if (/gebruik|uitgegeven|actief|active|toegewezen|in use/.test(s)) return 'IN_GEBRUIK'
  return null
}

/**
 * Opzegtermijn naar dagen: een getal = dagen; "30 dagen", "1 maand" (= 30 dagen,
 * zakelijke telecomcontracten rekenen in maanden), "2 maanden", "4 weken".
 * "geen"/"0" = 0. Onleesbaar -> `null` (nooit gokken).
 */
export function parseOpzegtermijnDagen(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isInteger(raw) && raw >= 0 ? raw : null
  const s = schoon(String(raw))
  if (!s) return null
  if (s === 'geen' || s === 'nvt' || s === 'n.v.t.') return 0
  const m = /^(\d+)\s*(dag|dagen|d|week|weken|w|maand|maanden|mnd|mnd\.|m)?$/.exec(s)
  if (!m) return null
  const n = Number(m[1])
  const eenheid = m[2] ?? 'dagen'
  if (eenheid.startsWith('w')) return n * 7
  if (eenheid.startsWith('m')) return n * 30
  return n
}

/** Verlengingsregime uit vrije tekst; onbekend -> `null`. */
export function parseVerlengingsregime(raw: string | null | undefined): AbonnementVerlengingsregime | null {
  const s = schoon(raw)
  if (!s) return null
  if (/stilzwijgend|automatisch|auto|maandelijks opzegbaar|doorlopend|onbepaald/.test(s)) return 'STILZWIJGEND'
  if (/vast|bepaald|eenmalig|loopt af|eindigt/.test(s)) return 'VAST'
  return null
}

/** DATA voor data-only SIM's (tablet, tracker, router, M2M); anders TELEFOON. */
export function parseTelefonieType(raw: string | null | undefined): TelefonieAbonnementType {
  const s = schoon(raw)
  if (/data|tablet|ipad|tracker|router|m2m|modem|4g|5g/.test(s) && !/spraak|bellen|telefoon|mobiel/.test(s)) {
    return 'DATA'
  }
  return 'TELEFOON'
}
